import { Box, Button, Stack, Typography } from '@mui/material'
import { IconButton } from 'utoniq-ui'

import { UserPaymentType } from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { ContentFrame } from '@/components/layouts/ContentFrame'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { individualDisplayResponsive } from '@/core/individual'

export * from './fragments'

export type State = {
  loading: boolean
  isKonbini: boolean
  purchaseOrderId: string
  paymentType?: UserPaymentType | null
  voucherUrl: string | null
  paymentDeadline: string | null
  pendingPurchaseItemId: string
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const StoreItemPurchaseCompleteTemplate: Template<State, Action> = ({
  state,
}) => {
  const { t } = useTranslation('storeItemPurchaseComplete')
  const { routerPages } = useRouter()

  const displayResponsive = individualDisplayResponsive()

  return (
    <BaseLayout
      margin
      header={{
        title: state.loading
          ? ''
          : state.isKonbini
          ? t('titleKonbini')
          : t('title'),
        left: (
          <IconButton.Back edge="start" size="medium" href={routerPages.Top} />
        ),
        ...(displayResponsive
          ? {
              responsive: true,
              maxWidth: 1120,
            }
          : {}),
      }}
      maxWidth={displayResponsive ? 600 : undefined}
    >
      {!state.loading && (
        <ResultCell
          isKonbini={state.isKonbini}
          paymentDeadline={state.paymentDeadline ?? ''}
        />
      )}
      {!state.loading && (
        <ButtonCell
          purchaseOrderId={state.purchaseOrderId}
          isKonbini={state.isKonbini}
          voucherUrl={state.voucherUrl}
          pendingPurchaseItemId={state.pendingPurchaseItemId}
        />
      )}
    </BaseLayout>
  )
}

const ResultCell: Cell<{ isKonbini: boolean; paymentDeadline: string }> = ({
  isKonbini,
  paymentDeadline,
}) => {
  const { t } = useTranslation('storeItemPurchaseComplete')

  return (
    <CellFrame sx={{ pt: 6, pb: 5 }}>
      <ContentFrame>
        <Typography variant="h5" align="center">
          {t(isKonbini ? 'completeMessageKonbini' : 'completeMessage')}
        </Typography>
        <Typography variant="body2" align="center" sx={{ pt: 1 }}>
          {t(isKonbini ? 'completeDescMessageKonbini' : 'completeDescMessage')}
        </Typography>
        {isKonbini && (
          <Box
            sx={{
              borderColor: 'error.main',
              borderWidth: 1,
              borderStyle: 'solid',
              py: 2,
              mt: 6,
            }}
          >
            <Typography variant="subtitle1" color="error.main" align="center">
              {t('paymentDeadline', { date: paymentDeadline })}
            </Typography>
          </Box>
        )}
      </ContentFrame>
    </CellFrame>
  )
}

const ButtonCell: Cell<{
  purchaseOrderId: string
  pendingPurchaseItemId: string
  isKonbini: boolean
  voucherUrl?: string | null
}> = ({ purchaseOrderId, pendingPurchaseItemId, voucherUrl, isKonbini }) => {
  const { t } = useTranslation('storeItemPurchaseComplete')
  const { routerPages, router, generatePath } = useRouter()
  const { onClick: onClickVoucherUrl } = useBrowserLink(voucherUrl ?? '')

  return (
    <CellFrame sx={{ pt: 5 }}>
      <ContentFrame>
        <Stack spacing={4} alignItems="center" sx={{ py: 5 }}>
          <Button
            variant="contained"
            size="large"
            sx={{ width: 244 }}
            onClick={
              voucherUrl
                ? onClickVoucherUrl
                : () =>
                    router.replace(
                      generatePath(routerPages.StorePurchaseOrderDetail, {
                        purchaseOrderId,
                      }),
                      {
                        state: {
                          from: routerPages.StorePurchaseOrders,
                        },
                      }
                    )
            }
          >
            {t(isKonbini ? 'paymentProcedure' : 'checkOwnedTicket')}
          </Button>
          {isKonbini ? (
            <Button
              variant="text"
              size="large"
              sx={{ width: 244, py: 1, height: 'auto' }}
              href={generatePath(routerPages.PaymentStatusDetail, {
                pendingPurchaseId: pendingPurchaseItemId,
              })}
            >
              {t('paymentStatus', '決済状況を確認する')}
            </Button>
          ) : (
            <Button variant="text" href={routerPages.Top}>
              {t('topPage')}
            </Button>
          )}
        </Stack>
      </ContentFrame>
    </CellFrame>
  )
}
